import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { Col, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import {
  fetchEmailTemplates,
  invalidateEmailTemplates,
} from "src/redux/actions/emailTemplates";
import Truncate from "src/components/truncate";
import InfiniteScroll from "src/components/infiniteScroll";
import { InputWithIcon } from "src/components/input";
import EmptyMessage from "src/components/emptyMessage/index";
import EmailTemplateEmptyMessage from "src/legacy/images/no_template.svg";

import _ from "lodash";
import cx from "classnames";
import styles from "./templates.module.scss";

interface TemplatesProps {
  width: number;
  onClose: () => void;
  onCloseTemplate: () => void;
  selectedTemplate: any;
  onSelection: (template: any) => void;
}

const Templates: React.FC<TemplatesProps> = ({
  width,
  onClose,
  onCloseTemplate,
  selectedTemplate,
  onSelection
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [query, setQuery] = React.useState<string>("");

  const { templates, loading, nextPage, hasMoreItems } = useSelector(
    (state: any) => {
      const emailTemplates = _.get(state, "emailTemplate", {});
      const templates = _.get(emailTemplates, "items", []);

      const loading = _.get(emailTemplates, "loading", true);
      const totalPages = _.get(emailTemplates, "totalPages", 0);
      const loadMore = _.get(emailTemplates, "loadMore", false);
      const hasMoreItems = _.get(emailTemplates, "hasMoreItems", false);

      let nextPage: number | null = _.get(emailTemplates, "currentPage", 0) + 1;
      if (nextPage && nextPage >= totalPages) {
        nextPage = null;
      }

      return { templates, loading, nextPage, hasMoreItems };
    },
    _.isEqual,
  );

  useEffect(() => {
    dispatch(fetchEmailTemplates({ query, page: 0 }));
  }, []);

  const fetchEmailTemplatesForPage = (page) => {
    if (query) {
      const request = {
        query: query,
        page: page,
      };
      dispatch(fetchEmailTemplates(request));
    }
  };

  const loadMoreTemplates = () => {
    if (nextPage) {
      fetchEmailTemplatesForPage(nextPage);
    }
  };

  const handleSearch = (e) => {
    dispatch(invalidateEmailTemplates());

    const newQuery = e.target.value;
    setQuery(newQuery);

    dispatch(fetchEmailTemplates({ query: newQuery, page: 0 }));
  };

  const handleAddTemplateClick = () => {
    onCloseTemplate();
    navigate("/settings/email-template");
  };

  const handleTemplateSelection = (template: any) => {
    onSelection(template);
  };

  const renderEmptyMessage = () => {
    return (
      <div className={styles.emptyContainer}>
        <EmptyMessage
          width={450}
          heading="Type less, share more. Create reusable email templates for you and your teams."
          image={EmailTemplateEmptyMessage}
          action={{
            title: "Create new",
            icon: "paperflite-add",
            onClick: () => handleAddTemplateClick()
          }}
        />
      </div>
    );
  };

  return (
    <section className={cx(styles.container)}>
      <div className={styles.headerContainer}>
        <div className={styles.header}>
        <h3 className={styles.heading}>Select Template</h3>
        <i className={cx(styles.templateClose, "paperflite-close")} onClick={onClose}></i>
        </div>
        <div>
        <InputWithIcon
          icon="paperflite-search"
          placeholder="Type here to search"
          value={query}
          className={styles.searchInput}
          onChange={handleSearch}
        />
      </div>
      </div>

      <div className={styles.row}>
        {!loading && _.size(templates) == 0 && renderEmptyMessage()}
        <InfiniteScroll
          loading={loading}
          hasMore={hasMoreItems}
          loadMore={loadMoreTemplates}
          loader={() => {
            if (_.size(templates) === 0) {
              return (
                <div className={styles.placeholder}>
                  <TemplatesPlaceholder width={"70%"} />
                  <TemplatesPlaceholder width={"70%"} />
                  <TemplatesPlaceholder width={"70%"} />
                </div>
              );
            } else {
              return <TemplatesPlaceholder width={"70%"} />;
            }
          }}
        >
          {templates.map((template: any, index: number) => {
            return (
              <Row
                key={index} 
                onClick={() => handleTemplateSelection(template)}
                >
                <Col>
                  <div className={styles.template}>
                    <div className={cx(styles.name, { [styles.selectedTemplate]: template.id === selectedTemplate?.id })}>
                      <Truncate>{template.name}
                        {template.default && <span className={styles.default}>(Default)</span> }
                      </Truncate>
                    </div>
                  </div>
                </Col>
              </Row>
            );
          })}
        </InfiniteScroll>
      </div>
    </section>
  );
};

export default Templates;

export const TemplatesPlaceholder: React.FC<{ width: string }> = ({
  width,
}) => {
  return (
    <div className={styles.placeholderContainer} style={{ width: width }}>
      <div className={styles.placeholderLines}>
        <div className={styles.line} style={{ width: "100%" }}></div>
        <div className={styles.line} style={{ width: "80%" }}></div>
      </div>
    </div>
  );
};
